// src/components/reports/ReportGenerator.js
import toast from 'react-hot-toast';

// --- HELPERS ---
const parseDurationToFloat = (durationStr) => {
    if (!durationStr) return 0.0;
    const hMatch = durationStr.match(/(\d+)\s*h/);
    const mMatch = durationStr.match(/(\d+)\s*m/);
    return (hMatch ? parseInt(hMatch[1], 10) : 0) + (mMatch ? parseInt(mMatch[1], 10) : 0) / 60;
};

const formatFloatToDuration = (val) => {
    if (val === undefined || val === null || isNaN(val)) return "-";
    const isNegative = val < 0;
    const absoluteVal = Math.abs(val);
    const hours = Math.floor(absoluteVal);
    const minutes = Math.round((absoluteVal - hours) * 60);
    const timeStr = `${hours}h ${minutes}m`;
    return isNegative ? `-${timeStr}` : timeStr;
};

/**
 * MAIN GENERATOR FUNCTION
 */
export const generateReportPDF = (reportType, { summary, timeEntries, currentProperty, dateFilter, customDate, payrollData, user }) => {
    if (!summary || Object.keys(summary).length === 0) {
        toast.error("No data available to generate a PDF.");
        return null;
    }
    
    if (!window.jspdf) {
        toast.error("PDF Generator library not loaded.");
        return null;
    }

    const { jsPDF } = window.jspdf;
    const doc = new jsPDF();

    // --- COLORS ---
    const COLOR_PRIMARY = [23, 152, 193]; 
    const COLOR_SECONDARY = [241, 245, 249];
    const COLOR_TEXT_MAIN = [30, 41, 59]; 
    const COLOR_TEXT_LIGHT = [100, 116, 139];

    // --- 1. Setup Data ---
    let titleSuffix = "";
    if (reportType === 'WORK_ONLY') titleSuffix = "Work Hours Log";
    if (reportType === 'PAYROLL_ONLY') titleSuffix = "Payment Statement";
    if (reportType === 'COMBINED') titleSuffix = "Summary Report";

    const propertyName = currentProperty ? currentProperty.name : "All Properties";
    const propertyAddress = currentProperty ? `${currentProperty.address || ''}, ${currentProperty.city || ''}, ${currentProperty.state || ''} ${currentProperty.zip_code || ''}`.replace(/ ,/g,',').replace(/^,|,$/g,'').trim() : '';
    const generatedDate = new Date().toLocaleString();
    
    const generatedBy = user ? `${user.firstName} ${user.lastName}` : 'System';

    // --- 2. Header Logic ---
    const pageHeader = () => {
        doc.setFillColor(...COLOR_PRIMARY);
        doc.rect(0, 0, 210, 6, 'F'); 

        doc.setFontSize(20);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...COLOR_TEXT_MAIN);
        doc.text(propertyName, 14, 22);

        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(...COLOR_PRIMARY);
        doc.text(titleSuffix.toUpperCase(), 14, 29);

        // Status Badge
        if (['PAYROLL_ONLY', 'COMBINED'].includes(reportType) && payrollData && payrollData.status) {
            doc.setFontSize(10);
            doc.setFont("helvetica", "bold");
            if (payrollData.status === 'SENT') {
                doc.setTextColor(22, 163, 74);
                doc.text("PAYROLL SENT", 196, 22, { align: "right" });
            } else if (payrollData.status === 'APPROVED') {
                doc.setTextColor(22, 101, 52);
                doc.text("PAYROLL APPROVED", 196, 22, { align: "right" });
            } else {
                doc.setTextColor(234, 88, 12);
                doc.text("PAYROLL DRAFT", 196, 22, { align: "right" });
            }
        }

        if (propertyAddress) {
            doc.setFontSize(9);
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...COLOR_TEXT_LIGHT);
            doc.text(propertyAddress, 196, 30, { align: "right" });
        }
        
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.5);
        doc.line(14, 35, 196, 35);
    };

    const pageFooter = () => {
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(150, 150, 150);
            
            doc.text(`Generated by: ${generatedBy} | ${generatedDate}`, 14, doc.internal.pageSize.height - 10);
            
            doc.setTextColor(...COLOR_PRIMARY);
            doc.textWithLink("The WesternTech", 105, doc.internal.pageSize.height - 10, { url: 'https://thewesterntech.com/', align: 'center' });
            
            doc.setTextColor(150, 150, 150);
            const footerText = `Page ${i} of ${pageCount}`;
            doc.text(footerText, 196, doc.internal.pageSize.height - 10, { align: "right" });
        }
    };

    pageHeader();

    // --- 3. Report Meta Data ---
    let reportDateText = "";
    const now = new Date();
    const monthName = now.toLocaleString('default', { month: 'long' });
    const year = now.getFullYear();
    
    if (dateFilter === 'today') reportDateText = `${now.toLocaleDateString()}`;
    else if (dateFilter === 'month') reportDateText = `${monthName} ${year}`;
    else if (dateFilter === 'firstHalf') reportDateText = `${monthName} 1 - 15, ${year}`;
    else if (dateFilter === 'secondHalf') {
        const lastDayOfMonth = new Date(year, now.getMonth() + 1, 0).getDate();
        reportDateText = `${monthName} 16 - ${lastDayOfMonth}, ${year}`;
    } else {
         reportDateText = `${new Date(customDate.start + 'T00:00:00').toLocaleDateString()} to ${new Date(customDate.end + 'T00:00:00').toLocaleDateString()}`;
    }

    // 1. Calculate width of the Date Text first so we know where to put the label
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    const dateWidth = doc.getTextWidth(reportDateText);

    // 2. Render the Date (Value) at the Right Margin (196)
    doc.setTextColor(...COLOR_TEXT_MAIN);
    doc.text(reportDateText, 196, 45, { align: "right" });

    // 3. Render the Label to the left of the Date
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...COLOR_TEXT_LIGHT);
    // Calculation: RightMargin (196) - DateWidth - Spacing (2)
    doc.text("Reporting Period:", 196 - dateWidth - 2, 45, { align: "right" });

    let lastY = 55;
    
    // --- 4. Summary Table ---
    const rows = Object.values(summary).map(s => {
        const totalHours = s.total_hours;
        const overtimeHours = s.overtime_hours || "-"; 
        
        let payrollHours = "-";
        let notes = "";
        let splitActive = "";
        let remainingStr = totalHours;

        if (payrollData && payrollData.lines) {
            const line = payrollData.lines.find(l => l.employee_user_id === s.employee_user_id);
            if (line) {
                splitActive = "Yes";
                const totalFloat = parseDurationToFloat(s.total_hours);
                const payrollFloat = parseFloat(line.payroll_hours);
                const diffFloat = totalFloat - payrollFloat;
                payrollHours = formatFloatToDuration(payrollFloat);
                remainingStr = formatFloatToDuration(diffFloat);
                notes = line.notes || "";
            }
        }
        return { 
            name: s.employee_name, 
            total: totalHours, 
            overtime: overtimeHours, 
            split: splitActive, 
            payroll: payrollHours, 
            remaining: remainingStr, 
            notes: notes 
        };
    });

    const hasOvertimeData = rows.some(r => r.overtime !== '-');

    let tableHead = [];
    let tableBody = [];

    if (reportType === 'PAYROLL_ONLY') {
        if (!payrollData) {
            toast.error("No Payment Statement found for this period.");
            return null;
        }
        doc.setFontSize(12);
        doc.setFont("helvetica", "bold");
        doc.text("Payment Statement", 14, lastY);
        lastY += 10;

        tableHead = [['Employee Name', 'Payable Hours', 'Notes']];
        tableBody = rows
            .filter(r => r.split === 'Yes') 
            .map(r => [r.name, r.payroll, r.notes]);

        if (tableBody.length === 0) {
            toast.error("Statement exists but has no lines.");
            return null;
        }
    } else {
        // COMBINED REPORT
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...COLOR_TEXT_MAIN);
        doc.text("Work & Payroll Summary", 14, lastY);
        doc.setLineWidth(0.5);
        doc.setDrawColor(...COLOR_TEXT_LIGHT);
        doc.line(14, lastY + 2, 78, lastY + 2); 
        lastY += 5;

        let payrollHeader = 'Payroll Hrs';
        if (payrollData && payrollData.status) payrollHeader = `Payroll Hrs (${payrollData.status})`;
        
        if (hasOvertimeData) {
            tableHead = [['Employee Name', 'Total Hours', 'Overtime', 'Split?', payrollHeader, 'Remaining']];
            tableBody = rows.map(r => [r.name, r.total, r.overtime, r.split, r.payroll, r.remaining]);
        } else {
            tableHead = [['Employee Name', 'Total Hours', 'Split?', payrollHeader, 'Remaining']];
            tableBody = rows.map(r => [r.name, r.total, r.split, r.payroll, r.remaining]);
        }
    }

    doc.autoTable({
        startY: lastY,
        head: tableHead,
        body: tableBody,
        theme: 'grid',
        headStyles: { 
            fillColor: COLOR_PRIMARY, 
            textColor: 255, 
            fontStyle: 'bold',
            cellPadding: 4,
            valign: 'middle'
        },
        styles: { 
            font: "helvetica", 
            fontSize: 10, 
            textColor: COLOR_TEXT_MAIN, 
            cellPadding: 3, 
            valign: 'middle'
        },
        columnStyles: { 
            0: { halign: 'left', fontStyle: 'bold' }, 
            1: { halign: 'center' },
            2: { halign: 'center' },
            3: { halign: 'center' }, 
            4: { halign: 'center' }, 
            5: { halign: 'center' }  
        },
        didParseCell: (data) => {
            if (reportType !== 'PAYROLL_ONLY' && data.section === 'body') {
                if (hasOvertimeData && data.column.index === 2) {
                    const val = data.cell.raw;
                    if (val && val !== '-') {
                        data.cell.styles.textColor = [239, 68, 68]; // Red
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            }
        },
        didDrawPage: (data) => pageHeader(),
        margin: { top: 40 }
    });
    lastY = doc.lastAutoTable.finalY + 15;

    // ============================================================
    // --- SIGNATURE BLOCK (STACKED - OPTIMIZED LAYOUT) ---
    // ============================================================
    if (['PAYROLL_ONLY', 'COMBINED'].includes(reportType)) {
        // Space Check
        if (lastY + 120 > doc.internal.pageSize.height - 20) {
            doc.addPage();
            lastY = 40;
        }

        let sigY = lastY + 3; 
        
        doc.setDrawColor(150, 150, 150);
        doc.setLineWidth(0.5);
        doc.setTextColor(100, 100, 100);

        const marginX = 14;
        const pageWidth = 182; // Max writable width

        // ----------------------------------------------------
        // 1. EMPLOYEE SECTION (Top)
        // ----------------------------------------------------
        let signatureLabel = "Verified By"; 
        const summaryKeys = Object.keys(summary);
        if (summaryKeys.length === 1) {
            const employeeName = summary[summaryKeys[0]].employee_name;
            signatureLabel = employeeName; 
        }

        // Signature Line
        doc.line(marginX, sigY, marginX + 80, sigY); 
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold"); // Bold Label
        doc.text(signatureLabel + " Signature", marginX, sigY + 5);
        
        // Date Line
        doc.setFont("helvetica", "normal");
        doc.text("Date: __________________", marginX + 90, sigY);

        // Text Bullets
        doc.setFontSize(8);
        doc.setTextColor(110, 110, 110);
        let textY = sigY + 12;
        const bulletIndent = 4;

        const printStackedBullet = (textEn, textEs) => {
            doc.setFont("helvetica", "normal");
            doc.text("\u2022", marginX, textY);
            
            const linesEn = doc.splitTextToSize(textEn, pageWidth - bulletIndent);
            doc.text(linesEn, marginX + bulletIndent, textY);
            textY += (linesEn.length * 3.5); 
            
            doc.setFont("helvetica", "italic");
            const linesEs = doc.splitTextToSize(textEs, pageWidth - bulletIndent);
            doc.text(linesEs, marginX + bulletIndent, textY);
            
            doc.setFont("helvetica", "normal");
            return textY + (linesEs.length * 3.5) + 2; 
        };

        textY = printStackedBullet(
            "I certify that this timesheet accurately represents all hours worked and I have not performed any work 'off-the-clock.'",
            "Certifico que esta hoja de horas representa con exactitud todas las horas trabajadas y que no he realizado ningún trabajo 'fuera de horario'."
        );

        textY = printStackedBullet(
            "I certify that I was provided the opportunity to take all uninterrupted meal and rest periods entitled by law.",
            "Certifico que se me brindó la oportunidad de tomar todos los períodos ininterrumpidos de comida y descanso a los que tengo derecho por ley."
        );

        textY = printStackedBullet(
            "I confirm I have not sustained any work-related injuries during this period that I have not already reported.",
            "Confirmo que no he sufrido ninguna lesión relacionada con el trabajo durante este período que no haya informado previamente."
        );

        // ----------------------------------------------------
        // 2. MANAGER SECTION (Bottom - Compacted Gap)
        // ----------------------------------------------------
        
        // Reduced gap to 10 units
        sigY = textY + 10; 

        doc.setDrawColor(150, 150, 150);
        doc.line(marginX, sigY, marginX + 80, sigY); 
        
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.setFont("helvetica", "bold"); // Bold Label
        doc.text("Manager Signature", marginX, sigY + 5);
        
        doc.setFont("helvetica", "normal");
        doc.text("Date: __________________", marginX + 90, sigY);

        // Manager Text (NOW USING BULLET STYLE)
        doc.setFontSize(8);
        doc.setTextColor(110, 110, 110);
        textY = sigY + 12;

        textY = printStackedBullet(
            "By signing, I verify that I have reviewed these time records for accuracy and confirmed they reflect the actual work performed and breaks taken by the employee.",
            "Al firmar, verifico que he revisado estos registros de tiempo para asegurar su exactitud y confirmo que reflejan el trabajo real realizado y los descansos tomados por el empleado."
        );

        lastY = textY + 10;
    }

    // --- 5. Detailed Tables ---
    if (reportType !== 'PAYROLL_ONLY' && timeEntries.length > 0) {
        if (lastY > 250) { doc.addPage(); lastY = 40; }
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(...COLOR_TEXT_MAIN);
        doc.text("Detailed Work Logs", 14, lastY);
        doc.setLineWidth(0.5);
        doc.setDrawColor(...COLOR_TEXT_LIGHT);
        doc.line(14, lastY + 2, 65, lastY + 2); 
        lastY += 10;

        const pairsByEmployee = timeEntries.reduce((acc, p) => {
            acc[p.employee_name] = acc[p.employee_name] || [];
            acc[p.employee_name].push(p);
            return acc;
        }, {});
        
        for (const employeeName in pairsByEmployee) {
            if (lastY > 240) { doc.addPage(); lastY = 40; }
            
            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            doc.setTextColor(...COLOR_PRIMARY);
            doc.text(employeeName, 14, lastY);
            lastY += 2; 
            
            const detailedBody = pairsByEmployee[employeeName].map(p => ([
                new Date(p.start_time).toLocaleDateString('en-US'),
                p.start_time ? new Date(p.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '-',
                p.end_time ? new Date(p.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) : '-',
                p.duration,
                p.status
            ]));
            
            doc.autoTable({
                startY: lastY + 2,
                head: [['Date', 'Start Time', 'End Time', 'Duration', 'Status']],
                body: detailedBody,
                theme: 'plain', 
                headStyles: { fillColor: COLOR_SECONDARY, textColor: COLOR_TEXT_MAIN, fontStyle: 'bold', lineWidth: 0 },
                styles: { font: "helvetica", fontSize: 9, cellPadding: 2, lineColor: [230, 230, 230], lineWidth: 0.1 },
                
                didParseCell: (data) => {
                    if (data.section === 'body' && data.column.index === 3) {
                        const durationStr = data.cell.raw;
                        if (typeof durationStr === 'string') {
                            const hourMatch = durationStr.match(/(\d+)h/);
                            const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;

                            if (hours >= 12) {
                                data.cell.styles.textColor = [220, 38, 38]; 
                                data.cell.styles.fontStyle = 'bold';
                            } 
                            else if (hours >= 8 && hasOvertimeData) {
                                data.cell.styles.textColor = [234, 88, 12]; 
                            }
                        }
                    }
                },

                didDrawCell: (data) => {
                    if (data.section === 'body' && data.column.index === 4) {
                        const status = data.cell.raw; 
                        const x = data.cell.x;
                        const y = data.cell.y;
                        const dim = 8;
                        const offsetX = 2;
                        const offsetY = (data.cell.height - dim) / 2; 

                        if (status === 'Completed') {
                            doc.setDrawColor(22, 163, 74); 
                            doc.setLineWidth(0.8);
                            doc.line(x + offsetX + 2, y + offsetY + 4, x + offsetX + 4, y + offsetY + 6);
                            doc.line(x + offsetX + 4, y + offsetY + 6, x + offsetX + 8, y + offsetY + 2);
                        } else {
                            doc.setDrawColor(234, 88, 12); 
                            doc.setLineWidth(0.8);
                            doc.line(x + offsetX + 2, y + offsetY + 2, x + offsetX + 6, y + offsetY + 6);
                            doc.line(x + offsetX + 6, y + offsetY + 2, x + offsetX + 2, y + offsetY + 6);
                        }
                    }
                },
                columnStyles: {
                    4: { cellPadding: { top: 2, left: 12, bottom: 2 } } 
                },
                didDrawPage: (data) => pageHeader(),
                margin: { top: 40 }
            });
            lastY = doc.lastAutoTable.finalY + 10;
        }
    }

    pageFooter();
    return doc;
};